// Settings panel — Self-SMS appointment reminders.
//
// Operator opts in to text themselves N minutes before any appointment.
// Cron /api/cron/self-appt-reminders.js (every 5 min) drains the queue.
//
// Defaults to enabled @ 30 min before. The "send to" phone defaults
// to the operator's CRM Twilio number but most agents want their
// personal cell — we let them override.

import React from 'react';
import { MessageSquare, RefreshCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';

const LEAD_OPTIONS = [
  { v: 5,    label: '5 minutes before' },
  { v: 10,   label: '10 minutes before' },
  { v: 15,   label: '15 minutes before' },
  { v: 30,   label: '30 minutes before' },
  { v: 60,   label: '1 hour before' },
  { v: 120,  label: '2 hours before' },
  { v: 1440, label: '1 day before' },
];

export default function SelfReminderPanel({ currentUser, showToast = () => {} }) {
  const [prefs, setPrefs] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]   = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase.from('subscriptions')
      .select('self_appt_reminder_enabled, self_appt_reminder_minutes_before, self_sms_to_phone_e164, twilio_phone_e164')
      .eq('owner_id', currentUser.id).maybeSingle();
    setPrefs(data || { self_appt_reminder_enabled: true, self_appt_reminder_minutes_before: 30, self_sms_to_phone_e164: null });
    setLoading(false);
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    const next = { ...prefs, ...patch };
    const { error } = await supabase.from('subscriptions').update(patch).eq('owner_id', currentUser.id);
    if (error) showToast(`Save failed: ${error.message}`);
    else { setPrefs(next); showToast('Saved.'); }
    setBusy(false);
  };

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">Loading reminder preferences…</div>;

  const fallbackTo = prefs?.self_sms_to_phone_e164 || prefs?.twilio_phone_e164 || '—';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-sky-600"/>
          <h3 className="font-bold text-slate-900">Text me before my appointments</h3>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>
      <p className="text-xs text-slate-500">
        Get a personal SMS reminder for every appointment on your CRM calendar.
        Pairs well with Google Calendar's native push if you've connected that too.
      </p>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox"
          checked={prefs?.self_appt_reminder_enabled !== false}
          onChange={e => save({ self_appt_reminder_enabled: e.target.checked })}
          disabled={busy}/>
        Enable self-SMS reminders
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Lead time</label>
          <select
            value={prefs?.self_appt_reminder_minutes_before ?? 30}
            onChange={e => save({ self_appt_reminder_minutes_before: Number(e.target.value) })}
            disabled={busy || !prefs?.self_appt_reminder_enabled}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white">
            {LEAD_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Send to phone</label>
          <input
            type="tel"
            value={prefs?.self_sms_to_phone_e164 || ''}
            onChange={e => setPrefs(p => ({ ...p, self_sms_to_phone_e164: e.target.value }))}
            onBlur={e => save({ self_sms_to_phone_e164: e.target.value.trim() || null })}
            placeholder="+1 555 555 0100 (your personal cell)"
            disabled={busy || !prefs?.self_appt_reminder_enabled}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white font-mono"
          />
          <div className="text-[10px] text-slate-500 mt-1">Defaults to your CRM number ({fallbackTo}) if blank.</div>
        </div>
      </div>
    </div>
  );
}
